package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration.Companion.minutes

/**
 * SH-210: a job interview with an applicant, an event for the team members taking part.
 *
 * Stored in its own collection `interviews/{id}` (written only by functions/hiringPipeline.js),
 * never as a shift: app versions before SH-210 read every `shifts` document as work (they price
 * it into shift_financials, count it in staffing and demand, offer an unassigned one on the
 * marketplace, and their full-document saves would drop an unknown `kind` field and turn it into
 * a plain shift). A separate collection is invisible to them; this version merges interviews
 * into the roster grid, the person's schedule and the calendar feed, visually distinct, and a
 * paid one into the labour cost ([labourShifts]).
 *
 * GDPR: the only data of the candidate here is [candidateName] (no e-mail, no phone). Deleting
 * the application (by a manager, or its retention) anonymises its interviews: the name and the
 * note go, the times stay for the labour cost already counted.
 */
data class Interview(
    val id: String,
    val workplaceId: String,
    /** The roster month of [date] (`workplaceId_YYYY_MM`). */
    val rosterId: String,
    /** "YYYY-MM-DD". */
    val date: String,
    /** Local wall-clock times of the workplace, "YYYY-MM-DDTHH:mm:ss". */
    val start: String,
    val end: String,
    val durationMinutes: Int,
    val postId: String?,
    val applicationId: String?,
    /** Null once the application is deleted. */
    val candidateName: String?,
    /** The post's title (the job), for the event's name. */
    val jobTitle: String = "",
    val location: String? = null,
    val onlineUrl: String? = null,
    val note: String? = null,
    val participantIds: List<String> = emptyList(),
    /** Paid: counts in the labour cost and hours like a shift. Unpaid: neither. */
    val paid: Boolean = false,
    val status: InterviewStatus = InterviewStatus.SCHEDULED,
    /** The calendar invitation's SEQUENCE: 0 at first, +1 with each change sent. */
    val sequence: Int = 0,
    val createdAt: String = "",
    val createdBy: String? = null,
    val updatedAt: String = "",
    val updatedBy: String? = null
) {
    val isCancelled: Boolean get() = status == InterviewStatus.CANCELLED

    /** "HH:mm" of the start and the end. */
    val startTime: String get() = start.drop(11).take(5)
    val endTime: String get() = end.drop(11).take(5)

    /** Hours, as a shift's. */
    val hours: Double get() = calculateDurationHours(start, end)

    /** Whether [userId] takes part. */
    fun involves(userId: String): Boolean = userId in participantIds

    /**
     * The interview as work for the labour cost: one shift per participant, in memory only (never
     * saved, ids `interview_<id>_<uid>`), when it is paid and not cancelled. The day's labour cost
     * and the budget price them as they price a shift without a cost snapshot (from the person's
     * pay terms), and count their hours. Unpaid interviews are no work: no cost and no hours
     * (they are not working time in the working-time records either).
     */
    fun labourShifts(): List<Shift> {
        if (!paid || isCancelled) return emptyList()
        return participantIds.distinct().map { uid ->
            Shift(
                id = labourShiftId(id, uid),
                rosterId = rosterId,
                date = date,
                dailyRecordId = DailyRecordIds.of(workplaceId, date),
                requiredRole = null,
                userId = uid,
                userDisplayName = null,
                actualUserId = null,
                isPublished = true,
                hasUnpublishedChanges = false,
                version = sequence,
                plannedTime = TimeRange(start, end),
                actualTime = null,
                conflictMetadata = null,
                metadata = AuditMetadata(createdAt, createdBy.orEmpty(), updatedAt, updatedBy),
                absenceId = null,
                tags = listOf(LABOUR_TAG)
            )
        }
    }

    companion object {
        const val MIN_MINUTES = 15
        const val MAX_MINUTES = 8 * 60
        const val MAX_PARTICIPANTS = 10
        const val MAX_LOCATION = 200
        const val MAX_URL = 500
        const val MAX_NOTE = 1000
        /** The tag of [labourShifts]' shifts. */
        const val LABOUR_TAG = "INTERVIEW"

        fun labourShiftId(interviewId: String, userId: String) = "interview_${interviewId}_$userId"

        /** Whether [shift] is an interview's labour (never a stored shift). */
        fun isLabourShift(shift: Shift): Boolean = shift.id.startsWith("interview_") && LABOUR_TAG in shift.tags

        /** "YYYY-MM-DDTHH:mm:00" [minutes] after [start] (local wall-clock time, no zone). */
        fun endOf(start: String, minutes: Int): String {
            val begin = LocalDateTime.parse(start.take(19).let { if (it.length == 16) "$it:00" else it })
            return (begin.toInstant(TimeZone.UTC) + minutes.minutes).toLocalDateTime(TimeZone.UTC).toString().let {
                if (it.length == 16) "$it:00" else it.take(19)
            }
        }
    }
}

enum class InterviewStatus {
    SCHEDULED, CANCELLED;

    companion object {
        fun fromId(id: String?): InterviewStatus = entries.firstOrNull { it.name == id } ?: SCHEDULED
    }
}

/** The paid interviews of [interviews] as labour shifts ([Interview.labourShifts]). */
fun List<Interview>.labourShifts(): List<Shift> = flatMap { it.labourShifts() }

/**
 * SH-210: what a reader asks for when scheduling or moving an interview ([interviewId] null: a new
 * one). [startsAt] is the workplace's local "YYYY-MM-DDTHH:mm".
 */
data class InterviewRequest(
    val postId: String,
    val applicationId: String,
    val interviewId: String? = null,
    val startsAt: String,
    val durationMinutes: Int,
    val location: String? = null,
    val onlineUrl: String? = null,
    val note: String? = null,
    val participantIds: List<String> = emptyList(),
    val paid: Boolean = false,
    /** Send the candidate the invitation (or its update) with the calendar file. */
    val sendInvitation: Boolean = true
)

/** What happened to the candidate's e-mail. */
enum class CandidateMail { QUEUED, NOT_REQUESTED, NO_EMAIL, ALREADY_SENT }

/** Another event of a participant at the same time (a warning, never a refusal). */
data class InterviewClash(val userId: String, val start: String, val end: String, val isInterview: Boolean)

/** The server's answer to scheduling or moving an interview. */
data class InterviewResult(
    val interviewId: String,
    val sequence: Int,
    val mail: CandidateMail,
    val clashes: List<InterviewClash> = emptyList()
)

/** Why an interview cannot be scheduled; the dialog names the field. */
class InvalidInterviewException(val problem: Problem) : Exception("Invalid interview: $problem") {
    enum class Problem { TIME_INVALID, LENGTH_INVALID, PLACE_REQUIRED, LINK_INVALID, TOO_LONG, PARTICIPANTS_TOO_MANY }
}
